<template>
  <div class="partner-shell partner-admin">
    <header class="workspace-header">
      <div class="workspace-heading">
        <h1 class="workspace-title">{{ $t('partnerAdmin.title') }}</h1>
        <p v-if="adminEmail" class="workspace-subtitle">{{ $t('partnerAdmin.signedInAs', { email: adminEmail }) }}</p>
      </div>
      <div v-if="adminEmail" class="workspace-actions">
        <button type="button" class="secondary-btn" :disabled="isLoading" @click="loadPartners">
          <span class="material-icons-outlined">refresh</span>
          {{ $t('partnerAdmin.refresh') }}
        </button>
      </div>
    </header>

    <div v-if="!authResolved || isLoading && !partners" class="workspace-state">
      <i class="material-icons spinning">refresh</i>
    </div>

    <div v-else-if="!user" class="workspace-state stacked">
      <span>{{ $t('partnerAdmin.loginHint') }}</span>
      <button type="button" class="primary-btn" @click="showLoginModal">{{ $t('partnerAdmin.login') }}</button>
    </div>

    <div v-else-if="loadError" class="workspace-state stacked">
      <span>{{ loadError }}</span>
    </div>

    <template v-else-if="partners">
      <nav class="status-tabs" :aria-label="$t('partnerAdmin.filterLabel')">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="status-tab"
          :class="{ active: filter === tab.key }"
          :aria-pressed="filter === tab.key"
          @click="filter = tab.key"
        >
          {{ tab.label }}
          <span class="tab-count">{{ tab.count }}</span>
        </button>
      </nav>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{{ $t('partnerAdmin.columns.partner') }}</th>
              <th>{{ $t('partnerAdmin.columns.contact') }}</th>
              <th>{{ $t('partnerAdmin.columns.status') }}</th>
              <th>{{ $t('partnerAdmin.columns.rate') }}</th>
              <th>{{ $t('partnerAdmin.columns.invitees') }}</th>
              <th>{{ $t('partnerAdmin.columns.netAmount') }}</th>
              <th>{{ $t('partnerAdmin.columns.pending') }}</th>
              <th>{{ $t('partnerAdmin.columns.available') }}</th>
              <th>{{ $t('partnerAdmin.columns.paid') }}</th>
              <th>{{ $t('partnerAdmin.columns.appliedAt') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="partner in visiblePartners"
              :key="partner.uid"
              class="partner-row"
              :class="{ selected: selectedUid === partner.uid }"
              tabindex="0"
              @click="selectPartner(partner.uid)"
              @keydown.enter="selectPartner(partner.uid)"
            >
              <td>
                <strong class="cell-title">{{ partner.application.name }}</strong>
                <span class="cell-sub">{{ partner.email }}</span>
              </td>
              <td>{{ partner.application.contact }}</td>
              <td><span class="status-tag" :class="statusClass(partner.status)">{{ $t(`partnerAdmin.status.${partner.status}`) }}</span></td>
              <td class="num">{{ percent(partner.commissionRate) }}</td>
              <td class="num">{{ partner.invitees }}</td>
              <td class="num">{{ money(partner.totals.netAmount, partner.totals.currency) }}</td>
              <td class="num">{{ money(partner.totals.pending, partner.totals.currency) }}</td>
              <td class="num">{{ money(partner.totals.available, partner.totals.currency) }}</td>
              <td class="num">{{ money(partner.totals.paid, partner.totals.currency) }}</td>
              <td>{{ date(partner.appliedAt) }}</td>
            </tr>
            <tr v-if="!visiblePartners.length">
              <td colspan="10" class="empty-row">{{ $t('partnerAdmin.empty') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <section v-if="detail" ref="detail" class="detail">
        <header class="detail-header">
          <div>
            <h2 class="detail-title">{{ detail.partner.application.name }}</h2>
            <span class="cell-sub">{{ detail.partner.email }}<template v-if="detail.code"> · {{ $t('partnerAdmin.code') }} {{ detail.code }}</template></span>
          </div>
          <span class="status-tag" :class="statusClass(detail.partner.status)">{{ $t(`partnerAdmin.status.${detail.partner.status}`) }}</span>
        </header>

        <div class="detail-grid">
          <div class="workspace-panel info-panel">
            <h3 class="panel-title">{{ $t('partnerAdmin.application') }}</h3>
            <dl class="info-list">
              <template v-for="field in applicationRows" :key="field.key">
                <dt>{{ field.label }}</dt>
                <dd>
                  <a v-if="field.key === 'profileUrl' && field.value" :href="field.value" target="_blank" rel="noopener noreferrer">{{ field.value }}</a>
                  <template v-else>{{ field.value || '—' }}</template>
                </dd>
              </template>
              <dt>{{ $t('partnerAdmin.appliedAt') }}</dt>
              <dd>{{ date(detail.partner.appliedAt) }}</dd>
            </dl>
            <h3 class="panel-title">{{ $t('partnerAdmin.payoutInfo') }}</h3>
            <dl class="info-list">
              <dt>{{ $t('partnerAdmin.payoutMethod') }}</dt>
              <dd>{{ detail.partner.payout.method || '—' }}</dd>
              <dt>{{ $t('partnerAdmin.payoutAccount') }}</dt>
              <dd>{{ detail.partner.payout.account || '—' }}</dd>
              <dt>{{ $t('partnerAdmin.payoutAccountName') }}</dt>
              <dd>{{ detail.partner.payout.accountName || '—' }}</dd>
            </dl>
          </div>

          <div class="workspace-panel action-panel">
            <div v-if="['pending', 'rejected'].includes(detail.partner.status)" class="action-block">
              <h3 class="panel-title">{{ $t('partnerAdmin.review') }}</h3>
              <p class="hint">{{ $t('partnerAdmin.reviewHint') }}</p>
              <input v-model.trim="rejectNote" class="text-input" :placeholder="$t('partnerAdmin.rejectNotePlaceholder')">
              <div class="button-row">
                <button type="button" class="primary-btn" :disabled="isActing" @click="act('approve')">{{ $t('partnerAdmin.approve') }}</button>
                <button
                  v-if="detail.partner.status === 'pending'"
                  type="button"
                  class="danger-btn"
                  :disabled="isActing"
                  @click="act('reject', { note: rejectNote })"
                >
                  {{ $t('partnerAdmin.reject') }}
                </button>
              </div>
            </div>

            <template v-if="detail.partner.approvedAt">
              <div class="action-block">
                <h3 class="panel-title">{{ $t('partnerAdmin.payout') }}</h3>
                <p class="hint">{{ $t('partnerAdmin.payoutHint', { amount: money(detail.records.totals.available, detail.records.currency) }) }}</p>
                <div class="field-pair">
                  <input v-model.trim="payoutMethod" class="text-input" :placeholder="$t('partnerAdmin.payoutMethodPlaceholder')">
                  <input v-model.trim="payoutNote" class="text-input" :placeholder="$t('partnerAdmin.payoutNotePlaceholder')">
                </div>
                <div class="button-row">
                  <button
                    type="button"
                    class="primary-btn"
                    :disabled="isActing || detail.records.totals.available <= 0"
                    @click="act('payout', { method: payoutMethod, note: payoutNote })"
                  >
                    {{ $t('partnerAdmin.markPaid', { amount: money(detail.records.totals.available, detail.records.currency) }) }}
                  </button>
                </div>
              </div>

              <div class="action-block">
                <h3 class="panel-title">{{ $t('partnerAdmin.terms') }}</h3>
                <div class="field-pair">
                  <label class="inline-field">
                    <span class="field-label">{{ $t('partnerAdmin.ratePercent') }}</span>
                    <input v-model.number="termsRate" class="text-input" type="number" min="1" max="100" step="1">
                  </label>
                  <label class="inline-field">
                    <span class="field-label">{{ $t('partnerAdmin.months') }}</span>
                    <input v-model.number="termsMonths" class="text-input" type="number" min="1" step="1">
                  </label>
                </div>
                <div class="button-row">
                  <button type="button" class="secondary-btn" :disabled="isActing" @click="act('terms', { rate: termsRate / 100, months: termsMonths })">
                    {{ $t('partnerAdmin.saveTerms') }}
                  </button>
                </div>
              </div>

              <div class="action-block">
                <h3 class="panel-title">{{ $t('partnerAdmin.premium') }}</h3>
                <p class="hint">{{ membershipLine }}</p>
                <label class="inline-field">
                  <span class="field-label">{{ $t('partnerAdmin.premiumMonths') }}</span>
                  <input v-model.number="premiumMonths" class="text-input" type="number" min="1" step="1">
                </label>
                <div class="button-row">
                  <button type="button" class="secondary-btn" :disabled="isActing" @click="act('extendPremium', { months: premiumMonths })">
                    {{ $t('partnerAdmin.extendPremium') }}
                  </button>
                  <button
                    v-if="detail.membership.plan === 'partner'"
                    type="button"
                    class="danger-btn"
                    :disabled="isActing"
                    @click="act('revokePremium')"
                  >
                    {{ $t('partnerAdmin.revokePremium') }}
                  </button>
                </div>
              </div>

              <div class="action-block">
                <h3 class="panel-title">{{ $t('partnerAdmin.account') }}</h3>
                <div class="button-row">
                  <button
                    v-if="detail.partner.status === 'approved'"
                    type="button"
                    class="danger-btn"
                    :disabled="isActing"
                    @click="act('disable')"
                  >
                    {{ $t('partnerAdmin.disable') }}
                  </button>
                  <button
                    v-if="detail.partner.status === 'disabled'"
                    type="button"
                    class="secondary-btn"
                    :disabled="isActing"
                    @click="act('enable')"
                  >
                    {{ $t('partnerAdmin.enable') }}
                  </button>
                </div>
              </div>
            </template>
            <p v-if="actionError" class="form-error">{{ actionError }}</p>
          </div>
        </div>

        <template v-if="detail.partner.approvedAt">
          <div class="stat-row">
            <div class="stat">
              <span class="field-label">{{ $t('partnerAdmin.columns.invitees') }}</span>
              <strong>{{ detail.records.totals.invitees }}</strong>
            </div>
            <div class="stat">
              <span class="field-label">{{ $t('partnerAdmin.paidInvitees') }}</span>
              <strong>{{ detail.records.totals.paidInvitees }}</strong>
            </div>
            <div class="stat">
              <span class="field-label">{{ $t('partnerAdmin.columns.netAmount') }}</span>
              <strong>{{ money(detail.records.totals.netAmount, detail.records.currency) }}</strong>
            </div>
            <div class="stat">
              <span class="field-label">{{ $t('partnerAdmin.columns.pending') }}</span>
              <strong>{{ money(detail.records.totals.pending, detail.records.currency) }}</strong>
            </div>
            <div class="stat">
              <span class="field-label">{{ $t('partnerAdmin.columns.available') }}</span>
              <strong>{{ money(detail.records.totals.available, detail.records.currency) }}</strong>
            </div>
            <div class="stat">
              <span class="field-label">{{ $t('partnerAdmin.columns.paid') }}</span>
              <strong>{{ money(detail.records.totals.paid, detail.records.currency) }}</strong>
            </div>
          </div>

          <div>
            <h3 class="section-title">{{ $t('partnerAdmin.invitees') }}</h3>
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{{ $t('partnerAdmin.user') }}</th>
                    <th>{{ $t('partnerAdmin.boundAt') }}</th>
                    <th>{{ $t('partnerAdmin.columns.status') }}</th>
                    <th>{{ $t('partnerAdmin.firstPaidAt') }}</th>
                    <th>{{ $t('partnerAdmin.columns.netAmount') }}</th>
                    <th>{{ $t('partnerAdmin.commission') }}</th>
                    <th>{{ $t('partnerAdmin.endsAt') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invitee in detail.records.invitees" :key="invitee.uid">
                    <td>{{ invitee.email }}</td>
                    <td>{{ date(invitee.boundAt) }}</td>
                    <td>{{ $t(`partner.inviteeStatus.${invitee.status}`) }}</td>
                    <td>{{ date(invitee.firstPaidAt) }}</td>
                    <td class="num">{{ money(invitee.netAmount, detail.records.currency) }}</td>
                    <td class="num">{{ money(invitee.commission, detail.records.currency) }}</td>
                    <td>{{ date(invitee.commissionEndsAt) }}</td>
                  </tr>
                  <tr v-if="!detail.records.invitees.length">
                    <td colspan="7" class="empty-row">{{ $t('partnerAdmin.noInvitees') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 class="section-title">{{ $t('partnerAdmin.commissions') }}</h3>
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{{ $t('partnerAdmin.paidAt') }}</th>
                    <th>{{ $t('partnerAdmin.user') }}</th>
                    <th>{{ $t('partnerAdmin.kind') }}</th>
                    <th>{{ $t('partnerAdmin.purchaseType') }}</th>
                    <th>{{ $t('partnerAdmin.columns.netAmount') }}</th>
                    <th>{{ $t('partnerAdmin.columns.rate') }}</th>
                    <th>{{ $t('partnerAdmin.commission') }}</th>
                    <th>{{ $t('partnerAdmin.columns.status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in detail.records.commissions" :key="item.id">
                    <td>{{ date(item.paidAt) }}</td>
                    <td>{{ item.inviteeEmail }}</td>
                    <td>{{ $t(`partner.commissionKind.${item.kind}`) }}</td>
                    <td>{{ $t(`partnerAdmin.purchase.${item.purchaseType}`) }}</td>
                    <td class="num">{{ money(item.netAmount, item.currency) }}</td>
                    <td class="num">{{ percent(item.rate) }}</td>
                    <td class="num">{{ money(item.commission, item.currency) }}</td>
                    <td>{{ $t(`partner.commissionStatus.${item.status}`) }}</td>
                  </tr>
                  <tr v-if="!detail.records.commissions.length">
                    <td colspan="8" class="empty-row">{{ $t('partnerAdmin.noCommissions') }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="detail.records.payouts.length">
            <h3 class="section-title">{{ $t('partnerAdmin.payouts') }}</h3>
            <div class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{{ $t('partnerAdmin.paidAt') }}</th>
                    <th>{{ $t('partnerAdmin.amount') }}</th>
                    <th>{{ $t('partnerAdmin.payoutMethod') }}</th>
                    <th>{{ $t('partnerAdmin.note') }}</th>
                    <th>{{ $t('partnerAdmin.commissionCount') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="payout in detail.records.payouts" :key="payout.id">
                    <td>{{ date(payout.createdAt) }}</td>
                    <td class="num">{{ money(payout.amount, payout.currency) }}</td>
                    <td>{{ payout.method || '—' }}</td>
                    <td>{{ payout.note || '—' }}</td>
                    <td class="num">{{ payout.commissionCount }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </template>
      </section>
    </template>
  </div>
</template>

<script>
import { useAuth } from '@/composables/auth'
import { useModals } from '@/composables/modals'
import {
  fetchAdminPartnerDetail,
  fetchAdminPartners,
  fetchAdminSession,
  formatDate,
  formatMoney,
  runAdminPartnerAction
} from '@/composables/usePartner'

const STATUS_TABS = ['pending', 'approved', 'disabled', 'rejected', 'all']

export default {
  name: 'PartnerAdmin',
  setup() {
    const { user, authResolved } = useAuth()
    const { showLoginModal } = useModals()
    return { user, authResolved, showLoginModal }
  },
  data() {
    return {
      adminEmail: '',
      partners: null,
      isLoading: false,
      loadError: '',
      filter: 'pending',
      selectedUid: '',
      detail: null,
      isActing: false,
      actionError: '',
      rejectNote: '',
      payoutMethod: '',
      payoutNote: '',
      termsRate: 30,
      termsMonths: 12,
      premiumMonths: 3
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale === 'zh' ? 'zh-CN' : 'en-US'
    },
    tabs() {
      return STATUS_TABS.map(key => ({
        key,
        label: this.$t(`partnerAdmin.tabs.${key}`),
        count: key === 'all' ? this.partners.length : this.partners.filter(item => item.status === key).length
      }))
    },
    visiblePartners() {
      return this.filter === 'all' ? this.partners : this.partners.filter(item => item.status === this.filter)
    },
    applicationRows() {
      const application = this.detail.partner.application
      return ['profileUrl', 'contact'].map(key => ({
        key,
        label: this.$t(`partner.form.${key}`),
        value: application[key]
      }))
    },
    membershipLine() {
      const membership = this.detail.membership
      const premium = this.detail.partner.premium
      if (premium && premium.reason === 'active_paid_plan') {
        return this.$t('partnerAdmin.premiumPaidPlan', { date: this.date(premium.until) })
      }
      if (membership.plan === 'partner') {
        return this.$t('partnerAdmin.premiumUntil', { date: this.date(membership.endDate) })
      }
      return this.$t('partnerAdmin.premiumNone', { plan: membership.planType || 'free' })
    }
  },
  watch: {
    user: {
      immediate: true,
      handler(value) {
        this.partners = null
        this.detail = null
        if (value) this.start()
      }
    }
  },
  methods: {
    date(value) {
      return formatDate(value, this.locale)
    },
    money(amount, currency) {
      return currency ? formatMoney(amount, currency, this.locale) : '—'
    },
    percent(rate) {
      return `${Math.round(rate * 100)}%`
    },
    statusClass(status) {
      return {
        'is-wait': status === 'pending',
        'is-good': status === 'approved',
        'is-bad': status === 'rejected' || status === 'disabled'
      }
    },
    async start() {
      this.loadError = ''
      try {
        this.adminEmail = (await fetchAdminSession()).email
      } catch (error) {
        this.loadError = error.status === 403
          ? this.$t('partnerAdmin.forbidden')
          : this.$t('partnerAdmin.loadFailed', { error: error.message })
        return
      }
      await this.loadPartners()
    },
    async loadPartners() {
      this.isLoading = true
      try {
        this.partners = (await fetchAdminPartners()).partners
        if (this.selectedUid) await this.selectPartner(this.selectedUid)
      } catch (error) {
        this.loadError = this.$t('partnerAdmin.loadFailed', { error: error.message })
      } finally {
        this.isLoading = false
      }
    },
    applyDetail(detail) {
      this.detail = detail
      this.termsRate = Math.round(detail.partner.commissionRate * 100)
      this.termsMonths = detail.partner.commissionMonths
      this.actionError = ''
    },
    async selectPartner(uid) {
      this.selectedUid = uid
      this.rejectNote = ''
      this.payoutMethod = ''
      this.payoutNote = ''
      this.applyDetail(await fetchAdminPartnerDetail(uid))
      this.$nextTick(() => this.$refs.detail?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
    },
    async act(action, payload = {}) {
      this.isActing = true
      this.actionError = ''
      try {
        this.applyDetail(await runAdminPartnerAction(this.selectedUid, action, payload))
        this.partners = (await fetchAdminPartners()).partners
      } catch (error) {
        this.actionError = this.$t('partnerAdmin.actionFailed', { error: error.message })
      } finally {
        this.isActing = false
      }
    }
  }
}
</script>

<style scoped>
.partner-admin {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  width: min(1400px, 100%);
}

.partner-admin .workspace-header {
  margin-bottom: 0;
}

.stacked {
  flex-direction: column;
}

.status-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.status-tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 34px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.status-tab.active {
  border-color: var(--border-active);
  background: var(--surface-active);
  color: var(--ink-primary);
  font-weight: 600;
}

.status-tab:focus-visible,
.partner-row:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.tab-count {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.partner-row {
  cursor: pointer;
}

.partner-row:hover td {
  background: var(--surface-subtle);
}

.partner-row.selected td {
  background: var(--surface-active);
}

.cell-title {
  display: block;
  font-weight: 600;
}

.cell-sub {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.detail {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
  padding-top: var(--space-6);
  border-top: 1px solid var(--border-default);
  scroll-margin-top: calc(var(--navbar-height) + var(--space-4));
}

.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
}

.detail-title {
  margin: 0 0 var(--space-1);
  font-size: var(--font-size-2xl);
  font-weight: 700;
}

.detail-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: var(--space-6);
}

.info-panel,
.action-panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5);
}

.panel-title {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
}

.info-list {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: var(--space-2) var(--space-4);
  margin: 0;
  font-size: var(--font-size-sm);
}

.info-list dt {
  color: var(--ink-tertiary);
}

.info-list dd {
  margin: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
}

.info-list a {
  color: var(--workspace-accent-dark);
}

.action-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
}

.action-block:last-of-type {
  padding-bottom: 0;
  border-bottom: none;
}

.hint {
  margin: 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.5;
}

.field-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
}

.inline-field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.button-row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

@media (max-width: 900px) {
  .detail-grid,
  .field-pair {
    grid-template-columns: 1fr;
  }
}
</style>
